import React, { useEffect, useRef, useState } from 'react';
import { Table, Tag, Modal, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ShiftReportItem, ReportFilter } from '../../model/report';
import { fetchWorkShiftReport } from '../../api/report';
import { exportExcel } from '../../utils/util';
import { ShiftThermalSlip80mm } from './components/thermal-slip-80mm';
import { printDocument } from '../../utils/print-utils';
import { useMeasuredRowScrollY, useTablePagination } from '../common/table-pagination';

interface Props {
    filter?: ReportFilter;
}

const ReportWorkShift: React.FC<Props> = ({ filter }) => {
    const pagination = useTablePagination();
    const { ref: tableRef, y: scrollY } = useMeasuredRowScrollY(420);
    const [shifts, setShifts] = useState<ShiftReportItem[]>([]);
    const [selectedShift, setSelectedShift] = useState<ShiftReportItem | null>(null);
    const [loading, setLoading] = useState(false);
    const shiftPrintRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await fetchWorkShiftReport(filter);
                if (Array.isArray(res)) {
                    setShifts(res);
                }
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        };
        load();
    }, [filter]);

    const totalShiftSales = shifts.reduce((acc, s) => acc + s.total_sales, 0);
    const totalCashSales = shifts.reduce((acc, s) => acc + s.cash_sales, 0);
    const totalDiff = shifts.reduce((acc, s) => acc + s.cash_diff, 0);

    const shiftColumns: ColumnsType<ShiftReportItem> = [
        {
            title: 'รหัสกะ',
            dataIndex: 'shift_no',
            key: 'shift_no',
            render: (val: string) => <span style={{ fontWeight: 700 }}>{val}</span>
        },
        { title: 'เครื่อง (Device)', dataIndex: 'device', key: 'device', render: (val: string) => val || 'POS-01' },
        { title: 'พนักงานแคชเชียร์', dataIndex: 'cashier_name', key: 'cashier_name' },
        { title: 'เวลาเปิดกะ', dataIndex: 'open_date', key: 'open_date' },
        { title: 'เวลาปิดกะ', dataIndex: 'close_date', key: 'close_date', render: (val: string) => val || <Tag color="orange">กำลังทำงาน</Tag> },
        {
            title: 'เงินทอนเปิดกะ',
            dataIndex: 'open_amount',
            key: 'open_amount',
            render: (val: number) => `฿${val.toLocaleString()}`
        },
        {
            title: 'ยอดขายเงินสด',
            dataIndex: 'cash_sales',
            key: 'cash_sales',
            render: (val: number) => `฿${val.toLocaleString()}`
        },
        {
            title: 'ยอดขายรวมกะ',
            dataIndex: 'total_sales',
            key: 'total_sales',
            render: (val: number) => <span style={{ fontWeight: 700, color: 'var(--primary, #FF7A59)' }}>฿${val.toLocaleString()}</span>
        },
        {
            title: 'เงินสดนับได้ตอนปิด',
            dataIndex: 'close_amount',
            key: 'close_amount',
            render: (val: number, record: ShiftReportItem) => record.status === 1 ? `฿${val.toLocaleString()}` : '-'
        },
        {
            title: 'ผลต่างเงินสด (เกิน/ขาด)',
            dataIndex: 'cash_diff',
            key: 'cash_diff',
            render: (val: number, record: ShiftReportItem) => {
                if (record.status === 0) return '-';
                if (val === 0) return <Tag color="green">ตรงพอดี</Tag>;
                if (val > 0) return <Tag color="blue">+฿{val.toLocaleString()} (เงินเกิน)</Tag>;
                return <Tag color="red">-฿{Math.abs(val).toLocaleString()} (เงินขาด)</Tag>;
            }
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            render: (status: number) => (
                <Tag color={status === 1 ? 'default' : 'processing'}>
                    {status === 1 ? 'ปิดกะแล้ว' : 'กำลังเปิดกะ'}
                </Tag>
            )
        },
        {
            title: 'พิมพ์สลิป 80mm',
            key: 'action',
            width: 140,
            render: (_: any, record: ShiftReportItem) => (
                <button
                    type="button"
                    className="btn-table-view"
                    onClick={() => setSelectedShift(record)}
                    title="ดูตัวอย่างและพิมพ์ใบสรุปปิดกะออกเครื่องพิมพ์สลิป 80mm"
                >
                    <FontAwesomeIcon icon="print" /> สลิป 80mm
                </button>
            )
        }
    ];

    const handleExportExcel = () => {
        const exportData = shifts.map(s => ({
            'รหัสกะ': s.shift_no,
            'เครื่อง': s.device,
            'พนักงานแคชเชียร์': s.cashier_name,
            'เวลาเปิดกะ': s.open_date,
            'เวลาปิดกะ': s.close_date || 'กำลังเปิดกะ',
            'เงินทอนเปิดกะ': s.open_amount,
            'ยอดขายเงินสด': s.cash_sales,
            'ยอดขายโอน/QR': s.transfer_sales,
            'ยอดขายรวมกะ': s.total_sales,
            'เงินสดนับได้ตอนปิด': s.status === 1 ? s.close_amount : '-',
            'ผลต่างเงินสด': s.status === 1 ? s.cash_diff : '-',
            'สถานะ': s.status === 1 ? 'ปิดกะแล้ว' : 'กำลังเปิดกะ'
        }));
        exportExcel('รายงานสรุปกะการทำงาน', exportData);
    };

    return (
        <div>
            {/* Shift KPI Summary Cards */}
            <div className="report-kpi-grid">
                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-sales">
                        <FontAwesomeIcon icon="user-clock" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">จำนวนกะการทำงาน</span>
                        <span className="kpi-value">{shifts.length} กะ</span>
                        <span className="kpi-subtext">กะที่เปิดอยู่ {shifts.filter(s => s.status === 0).length} กะ</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-profit">
                        <FontAwesomeIcon icon="money-bill-wave" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">ยอดขายรวมทุกกะ</span>
                        <span className="kpi-value" style={{ color: 'var(--primary, #FF7A59)' }}>฿{totalShiftSales.toLocaleString()}</span>
                        <span className="kpi-subtext">ยอดเงินสดในกะ ฿{totalCashSales.toLocaleString()}</span>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon-box kpi-bills" style={{ background: totalDiff >= 0 ? '#ECFDF5' : '#FEF2F2', color: totalDiff >= 0 ? '#10B981' : '#DC2626' }}>
                        <FontAwesomeIcon icon="scale-balanced" />
                    </div>
                    <div className="kpi-info-box">
                        <span className="kpi-label">ผลต่างเงินสดสุทธิ (Cash Diff)</span>
                        <span className="kpi-value" style={{ color: totalDiff >= 0 ? '#10B981' : '#DC2626' }}>
                            {totalDiff >= 0 ? `+฿${totalDiff.toLocaleString()}` : `-฿${Math.abs(totalDiff).toLocaleString()}`}
                        </span>
                        <span className="kpi-subtext">{totalDiff === 0 ? 'เงินสดตรงยอดพอดี' : totalDiff > 0 ? 'เงินเกินในลิ้นชัก' : 'เงินขาดในลิ้นชัก'}</span>
                    </div>
                </div>
            </div>

            {/* Shift Table Section */}
            <div className="report-card-section">
                <div className="report-section-header">
                    <span className="report-section-title">
                        <FontAwesomeIcon icon="clipboard-list" style={{ color: 'var(--primary, #FF7A59)' }} />
                        <span>ประวัติการเปิด-ปิดกะการทำงาน (Shift Z-Report)</span>
                    </span>
                    <button
                        type="button"
                        className="btn-report-action btn-excel"
                        onClick={handleExportExcel}
                    >
                        <FontAwesomeIcon icon="file-excel" /> ส่งออก Excel
                    </button>
                </div>

                <div ref={tableRef}>
                <Table
                    columns={shiftColumns}
                    dataSource={shifts}
                    rowKey="id"
                    pagination={pagination}
                    scroll={{ y: scrollY }}
                    loading={loading}
                />
                </div>
            </div>

            {/* Modal for 80mm Shift Z-Report Slip Printing */}
            <Modal
                title="ตัวอย่างสลิปปิดกะ (80mm Thermal Slip)"
                open={selectedShift !== null}
                onCancel={() => setSelectedShift(null)}
                footer={[
                    <Button key="close" onClick={() => setSelectedShift(null)}>
                        ปิด
                    </Button>,
                    <Button
                        key="print"
                        type="primary"
                        style={{ background: '#10B981', borderColor: '#10B981' }}
                        onClick={() => {
                            printDocument(shiftPrintRef.current, {
                                documentType: 'thermal-80mm',
                                title: `ใบสรุปปิดกะ ${selectedShift?.shift_no || ''}`
                            });
                        }}
                    >
                        <FontAwesomeIcon icon="print" /> สั่งพิมพ์ออกเครื่องสลิป 80mm
                    </Button>
                ]}
                width={380}
                centered
            >
                {selectedShift && (
                    <div style={{ padding: '10px 0', background: '#F8FAFC', display: 'flex', justifyContent: 'center' }}>
                        <ShiftThermalSlip80mm ref={shiftPrintRef} shift={selectedShift} />
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ReportWorkShift;
